<template>
	<div class="fixed inset-0 overflow-y-scroll bg-black bg-opacity-50 z-20" style="backdrop-filter: blur(5px); overscroll-behavior: contain">
		<div class="flex items-start justify-center text-center my-24">
			<div
				class="bg-white rounded-lg text-center shadow-xl w-3/5 md:w-3/5 lg:w-3/5 xl:w-3/5 xxl:w-3/5 2xl:w-3/5 justify-center relative"
				style="min-height: 400px"
				role="dialog"
				aria-modal="true"
			>
				<div class="py-2">
					<BaseHeader
						class="rounded-t-lg"
						:title="$t('premium_partner_activation_title').toString()"
						:subtitle="$t('premium_partner_activation_subtitle').toString()"
					>
						<font-awesome-icon :icon="['fas', 'times']" class="cursor-pointer text-xl" @click="closeModal" />
					</BaseHeader>

					<div class="px-10 py-4">
						<div class="w-full flex flex-col gap-6 text-left">
							<!-- Intro -->
							<div>
								<p class="text-sm text-gray-700 leading-6">
									{{ $t('premium_partner_activation_description') }}
								</p>
							</div>

							<!-- Requirement box -->
							<div class="p-4 border rounded bg-gray-50">
								<div class="font-semibold mb-2">
									{{ $t('premium_partner_requirement_title') }}
								</div>

								<p class="text-sm text-gray-700 leading-6">
									{{ $t('premium_partner_requirement_description') }}
								</p>
							</div>

							<!-- Contract block -->
							<div class="p-4 border rounded">
								<div class="font-semibold mb-4">
									{{ $t('premium_partner_contract_block_title') }}
								</div>

								<div class="flex flex-col gap-3">
									<div class="flex justify-start gap-12 items-center">
										<BaseButton @click="downloadContract()" style="min-width: 310px">
											{{ $t('premium_partner_contract_read') }}
										</BaseButton>

										<div class="flex">
											<BaseCheckbox
												ref="acceptPremiumPartnerContract"
												v-model="acceptPremiumPartnerContract"
												:label="$t('accept_premium_partner_contract_explanation')"
												:required="true"
												error-message=" "
											/>
										</div>
									</div>
								</div>
							</div>

							<!-- Privacy block -->
							<div class="p-4 border rounded">
								<div class="font-semibold mb-4">
									{{ $t('privacy_block_title') }}
								</div>

								<div class="flex flex-col gap-3">
									<div class="flex justify-start gap-12 items-center">
										<BaseButton @click="downloadDataProtection()" style="min-width: 310px">
											{{ $t('data_protection_read') }}
										</BaseButton>

										<div class="flex">
											<BaseCheckbox
												ref="acceptPrivacy"
												v-model="acceptPrivacy"
												:label="$t('accept_data_protection_explanation')"
												:required="true"
												error-message=" "
											/>
										</div>
									</div>
								</div>
							</div>

							<!-- Footer -->
							<Footer :is-back-button="false" :is-continue-button="true">
								<template #continueButton>
									<SaveButton @click="acceptContract()" :disabled="!canAcceptContract">
										{{ $t('premium_partner_activate') }}
									</SaveButton>
								</template>
							</Footer>
						</div>
					</div>
				</div>

				<!-- later optional -->
				<!-- <LoadingSpinner class="pt-2" v-else /> -->
			</div>
		</div>
	</div>
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader.vue';
import BaseCheckbox from '@/components/forms/inputs/BaseCheckbox.vue';
import BaseButton from '@/components/forms/buttons/BaseButton.vue';
import SaveButton from '@/components/forms/buttons/SaveButton.vue';
import Footer from '@/components/damagecases/Footer.vue';
import OnboardingRepository from '@/api/repositories/OnboardingRepository';
import PremiumLawyerOfficeRepository from '@/api/repositories/PremiumLawyerOfficeRepository';

export default {
	name: 'PremiumPartnerActivationModal',
	components: { Footer, SaveButton, BaseButton, BaseCheckbox, BaseHeader },
	emits: ['downloadPremiumPartnerContract', 'downloadDataProtection', 'close'],
	data() {
		return {
			acceptPremiumPartnerContract: false,
			acceptPrivacy: false,
		};
	},
	computed: {
		canAcceptContract() {
			return this.acceptPremiumPartnerContract && this.acceptPrivacy;
		},
	},
	methods: {
		closeModal() {
			this.$emit('close');
		},
		openDocument(response) {
			const blob = new Blob([response.data], { type: 'application/pdf' });
			const name = response.headers['content-disposition'].split('filename=')[1];
			if (this.isAppContainer) {
				const reader = new FileReader();
				reader.onload = function() {
					const arrayBuffer = this.result;
					// convert the ArrayBuffer to a comma-separated string
					const dataString = new Uint8Array(arrayBuffer).join(',');
					const messageData = {
						fileName: name,
						data: dataString,
					};
					// send the data to Swift
					window.webkit.messageHandlers.pdfHandler.postMessage(JSON.stringify(messageData));
				};
				reader.readAsArrayBuffer(blob);
			} else {
				const resolvingUrl = URL.createObjectURL(blob);
				const viewerUrl = `/blobviewer?blobUrl=${encodeURIComponent(resolvingUrl)}&title=${encodeURIComponent(name)}`;

				setTimeout(function() {
					window.open(viewerUrl, '_blank');
					window.URL.revokeObjectURL(viewerUrl);
				}, 200);
			}
		},
		acceptContract() {
			if (this.acceptPremiumPartnerContract && this.acceptPrivacy) {
				new PremiumLawyerOfficeRepository()
					.acceptContract()
					.then(response => {
						this.$swal(this.$t('general.success').toString(), this.$t('premium_partner_contract_accepted').toString(), 'success')
							.then(_ =>
							this.closeModal(),
						);
					})
					.catch(e => {
						console.error(e);
						this.$swal(this.$t('general.error').toString(), this.$t('premium_partner_contract_error').toString(), 'error');
					});
			}
		},
		downloadContract() {
			new PremiumLawyerOfficeRepository()
				.downloadPremiumPartnerContact()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
		downloadDataProtection() {
			new OnboardingRepository()
				.downloadDataProtection()
				.then(response => {
					this.openDocument(response);
				})
				.catch(e => {
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
	},
};
</script>
